// ---------------------------------------------------------
// Datei: DocumentPreviewModal.tsx
// Zweck: Vorschau-Modal für hochgeladene Dokumente (Bilder & PDFs)
// Besonderheiten:
// - Unterstützt Base64-Bilder, Blob-PDFs und externe URLs (HubSpot)
// - Responsive Fullscreen-Vorschau mit voller Scroll-Unterstützung
// - Actions: Schließen, Ersetzen (optional)
// Stand: 04.12.2025 (Issue #4 Fix: PDF-Vorschau nicht mehr abgeschnitten)
// ---------------------------------------------------------

import React, { useEffect, useState } from 'react';

interface DocumentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentName: string;
  documentType: 'image' | 'pdf';
  previewData: string; // Base64, Object URL oder externe URL (HubSpot Signed URL)
  onReplace?: () => void; // Optional: Ersetzen-Action
  // Ticket #1055 (Nutzer-Rückmeldung): Beschriftung eines Fotos, nur relevant wenn der
  // Aufrufer eine hat (z. B. ProjektvorbereitungBriefing bei Nicht-Ops-Managern, die den
  // Markierungs-Editor nicht öffnen dürfen) — wird als ausklappbarer Bereich unter der
  // Vorschau gezeigt statt permanent sichtbaren Text zu erzwingen.
  caption?: string | null;
}

// Hilfsfunktion: Prüft ob previewData eine externe URL ist
const isExternalUrl = (data: string): boolean => {
  return data.startsWith('http://') || data.startsWith('https://');
};

// Hilfsfunktion: Ermittelt den Dokumenttyp aus URL
const getDocTypeFromUrl = (url: string): 'image' | 'pdf' => {
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes('.pdf') || lowerUrl.includes('application/pdf')) {
    return 'pdf';
  }
  return 'image'; // Default: Bild (jpg, png, etc.)
};

const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  isOpen,
  onClose,
  documentName,
  documentType: propDocumentType,
  previewData,
  onReplace,
  caption,
}) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  
  // Bei externen URLs: Dokumenttyp aus URL ermitteln
  const documentType = isExternalUrl(previewData) 
    ? getDocTypeFromUrl(previewData) 
    : propDocumentType;

  // PDF Blob → Object URL konvertieren (nur für Base64, nicht für externe URLs)
  useEffect(() => {
    // Externe URLs brauchen keine Konvertierung
    if (isExternalUrl(previewData)) {
      setObjectUrl(previewData);
      return;
    }
    
    if (isOpen && documentType === 'pdf' && previewData) {
      // Wenn previewData ein Base64-String ist, konvertieren
      if (previewData.startsWith('data:application/pdf;base64,')) {
        const base64Data = previewData.split(',')[1];
        const binaryData = atob(base64Data);
        const bytes = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          bytes[i] = binaryData.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setObjectUrl(url);
      } else {
        // Bereits eine Object URL
        setObjectUrl(previewData);
      }
    }

    // Cleanup (nur für Blob URLs, nicht für externe URLs)
    return () => {
      if (objectUrl && !isExternalUrl(objectUrl)) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [isOpen, documentType, previewData]);

  // Modal schließen bei ESC
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    // pb-20: Platz für BottomNavigation (ca. 80px)
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4 pb-20">
      {/* Modal Container - max-h reduziert für BottomNav-Platz */}
      <div className="relative w-full h-full max-w-7xl max-h-[calc(90vh-60px)] bg-white dark:bg-gray-800 rounded-lg shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white truncate">
            {documentName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            aria-label="Schließen"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Preview Content - Scrollbar für breite PDFs */}
        <div className="flex-1 min-h-0 overflow-auto p-2 bg-gray-50 dark:bg-gray-900">
          {documentType === 'image' ? (
            /* Bild-Vorschau (Base64, Object URL oder externe URL) */
            <div className="flex items-center justify-center h-full overflow-auto">
              <img
                src={previewData}
                alt={documentName}
                className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                referrerPolicy="no-referrer"
              />
            </div>
          ) : documentType === 'pdf' && objectUrl ? (
            /* PDF-Vorschau - volle Breite und Höhe */
            <iframe
              src={objectUrl}
              title={documentName}
              className="w-full h-full rounded-lg shadow-lg"
              style={{ minHeight: 'calc(90vh - 200px)', minWidth: '100%' }}
            />
          ) : (
            /* Fehler-Fallback */
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-500 dark:text-gray-400">
                  Vorschau nicht verfügbar
                </p>
              </div>
            </div>
          )}
        </div>

        {caption && (
          <details className="border-t border-gray-200 dark:border-gray-700 px-4 py-2">
            <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-200">
              Beschriftung
            </summary>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
              {caption}
            </p>
          </details>
        )}

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Schließen
          </button>
          {onReplace && (
            <button
              onClick={() => {
                onClose();
                onReplace();
              }}
              className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Ersetzen
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
